import { useEffect, useState } from 'react';
import { useSimulation } from '../context/SimulationContext';
import { 
  Warning, 
  Lightning, 
  HeartBreak, 
  FirstAid,
  CheckCircle,
  XCircle,
  Info
} from '@phosphor-icons/react';

/**
 * Panel de alertas de eventos condicionales del escenario.
 * Muestra alertas cuando se disparan eventos debido a omisiones o demoras del estudiante.
 * Utilizado especialmente en el escenario IAM Demo.
 */
export default function EventAlertsPanel() {
  const { eventAlerts, setEventAlerts } = useSimulation();
  const [visibleAlerts, setVisibleAlerts] = useState([]);

  useEffect(() => {
    if (eventAlerts && eventAlerts.length > 0) {
      // Agregar timestamp de display a nuevas alertas
      const newAlerts = eventAlerts.map(alert => ({
        ...alert,
        displayTime: alert.displayTime || Date.now(),
        id: alert.id || `${Date.now()}-${Math.random()}`
      }));
      
      setVisibleAlerts(prev => {
        // Evitar duplicados
        const existingIds = new Set(prev.map(a => a.id));
        const uniqueNew = newAlerts.filter(a => !existingIds.has(a.id));
        return [...prev, ...uniqueNew];
      });
    }
  }, [eventAlerts]);

  // Auto-dismiss alerts after 12 seconds
  useEffect(() => {
    if (visibleAlerts.length === 0) return;

    const timer = setInterval(() => {
      const now = Date.now();
      setVisibleAlerts(prev => 
        prev.filter(alert => now - alert.displayTime < 12000)
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [visibleAlerts]);

  const dismissAlert = (alertId) => {
    setVisibleAlerts(prev => prev.filter(a => a.id !== alertId));
    setEventAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const getAlertStyles = (type) => {
    switch (type) {
      case 'critical':
        return {
          bg: 'bg-red-600',
          border: 'border-red-700',
          icon: <HeartBreak size={28} weight="fill" className="text-white animate-pulse" />,
          pulse: true
        };
      case 'warning':
        return {
          bg: 'bg-orange-500',
          border: 'border-orange-600',
          icon: <Warning size={28} weight="fill" className="text-white" />,
          pulse: false
        };
      case 'success':
        return {
          bg: 'bg-green-600',
          border: 'border-green-700',
          icon: <CheckCircle size={28} weight="fill" className="text-white" />,
          pulse: false
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-600',
          border: 'border-blue-700',
          icon: <Info size={28} weight="fill" className="text-white" />,
          pulse: false
        };
    }
  };

  if (visibleAlerts.length === 0) return null;

  return (
    <div 
      className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 space-y-3 max-w-2xl w-full px-4"
      data-testid="event-alerts-panel"
    >
      {visibleAlerts.map((alert) => {
        const styles = getAlertStyles(alert.type);
        
        return (
          <div
            key={alert.id}
            className={`
              ${styles.bg} ${styles.border} border-2
              rounded-lg shadow-2xl p-4
              transform transition-all duration-500 ease-out
              animate-slideDown
              ${styles.pulse ? 'animate-pulse-border' : ''}
            `}
            data-testid={`event-alert-${alert.event_type}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {styles.icon}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-white font-bold text-lg uppercase tracking-wide">
                    {alert.event_type === 'fatal' ? 'ALERTA CRÍTICA' :
                     alert.event_type === 'critico' ? 'COMPLICACIÓN' :
                     alert.event_type === 'complicacion' ? 'COMPLICACIÓN' :
                     alert.event_type === 'empeoramiento' ? 'DETERIORO' :
                     alert.event_type === 'mejoria' ? 'MEJORÍA' :
                     'EVENTO'}
                  </h4>
                  <button
                    onClick={() => dismissAlert(alert.id)}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    <XCircle size={24} />
                  </button>
                </div>
                
                <p className="text-white/90 mt-1 text-base">
                  {alert.message}
                </p>
                
                {alert.trigger_reason && (
                  <p className="text-white/70 text-sm mt-1 italic">
                    Causa: {alert.trigger_reason}
                  </p>
                )}
                
                {alert.action_required && (
                  <div className="mt-2 flex items-center gap-2 text-yellow-200">
                    <FirstAid size={18} weight="fill" />
                    <span className="text-sm font-medium">
                      Acción requerida: {alert.action_required}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Progress bar for auto-dismiss */}
            <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white/60 transition-all duration-1000 ease-linear"
                style={{ 
                  width: `${Math.max(0, 100 - ((Date.now() - alert.displayTime) / 120))}%`,
                  animation: 'shrink 12s linear forwards'
                }}
              />
            </div>
          </div>
        );
      })}
      
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        
        @keyframes pulse-border {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
          50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
        }
        
        .animate-slideDown {
          animation: slideDown 0.4s ease-out;
        }
        
        .animate-pulse-border {
          animation: pulse-border 1.5s infinite;
        }
      `}</style>
    </div>
  );
}
